import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import useSEO from '../hooks/useSEO';
import { schemaGraph, courseSchema } from '../utils/schema';
import { useStateContext } from '../hooks/useStateContext';
import { getStateByCode } from '../utils/states';

const CREDIT_LABELS = {
  core: 'Core', elective: 'Elective',
  agency: 'Agency', commissioners_standard: "Commissioner's Standard",
  contract_law: 'Contract Law', disclosure: 'Disclosure',
  fair_housing: 'Fair Housing', real_estate_legal_issues: 'RE Legal Issues', general: 'General',
};

export default function CourseDetailPage() {
  const { id } = useParams();
  const { addToCart, isInCart } = useCart();
  const { state } = useStateContext();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/courses/${id}`).then(r => setCourse(r.data.course)).finally(() => setLoading(false));
  }, [id]);

  // A course belongs to exactly one state, so trust the course's own state over the
  // browsing context — a Kansas course must never render Arizona/ADRE copy.
  const courseState = getStateByCode(course?.state) || state;

  const courseUrl = `https://steadyeducation.com/courses/${id}`;
  useSEO({
    title: course ? `${course.title} | Steady Education` : 'Course Details | Steady Education',
    description: course?.short_description || course?.description?.slice(0, 160) || `${courseState.name} real estate continuing education course.`,
    url: courseUrl,
    jsonLd: course ? schemaGraph({
      mainSchemas: [courseSchema(course, courseUrl)],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Courses', url: 'https://steadyeducation.com/courses' },
        { name: course.title, url: courseUrl },
      ],
    }) : undefined,
  });

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addToCart(id);
      navigate('/cart');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add to cart');
    } finally { setAdding(false); }
  };

  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;
  if (!course) return <div className="page-wrapper"><div className="container"><h2>Course not found</h2></div></div>;

  const owned = !!course.userEnrollment;
  const inCart = isInCart(id);

  return (
    <div className="page-wrapper">
      <div className="container">
        {/* Breadcrumb */}
        <div style={styles.breadcrumb}>
          <Link to={`/${state.slug}/courses`} style={styles.breadLink}>← Back to Courses</Link>
        </div>

        <div style={styles.layout} className="course-detail-layout">
          {/* Main */}
          <div style={styles.main} className="course-detail-main">
            <div style={styles.categoryRow}>
              <span className="badge badge-teal">{CREDIT_LABELS[course.credit_type] || 'General'}</span>
              {course.state_approved && <span className="badge badge-success">✓ {courseState.regulator} Approved</span>}
            </div>
            <h1 style={styles.title}>{course.title}</h1>
            <p style={styles.description}>{course.description}</p>

            {/* What you'll learn */}
            <div style={styles.learnBox} className="course-detail-after">
              <h3 style={styles.learnTitle}>What You'll Learn</h3>
              <div style={styles.learnGrid} className="course-detail-learn-grid">
                {[
                  `${courseState.name}-specific laws and regulations`,
                  `${courseState.regulator} compliance requirements`,
                  'Practical application in real transactions',
                  'Exam preparation with practice questions',
                ].map(item => (
                  <div key={item} style={styles.learnItem}>
                    <span style={styles.checkMark}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Course sections preview */}
            {course.sections?.length > 0 && (
              <div style={styles.sectionsBox} className="course-detail-after">
                <h3 style={styles.learnTitle}>Course Outline</h3>
                {course.sections.map((s, i) => (
                  <div key={s.id} style={styles.sectionItem}>
                    <span style={styles.sectionNum}>{i + 1}</span>
                    <span>{s.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={styles.sidebar} className="course-detail-sidebar">
            <div style={styles.sideCard}>
              <div style={styles.sideHeader}>
                <div style={styles.price}>${parseFloat(course.price).toFixed(2)}</div>
                <div style={styles.priceNote}>One-time payment • Lifetime access</div>
              </div>

              <div style={styles.sideMeta}>
                {[
                  ['⏱️', 'Credit Hours', `${course.credit_hours} Hours`],
                  ['📚', 'Category', CREDIT_LABELS[course.credit_type] || 'General'],
                  ['🏛️', 'Approved By', `${courseState.name} ${courseState.regulator}`],
                  ['📋', 'Course #', course.adre_course_number || 'N/A'],
                  ['💻', 'Format', '100% Online'],
                  ['🔄', 'Access', 'Unlimited Retakes'],
                ].map(([icon, label, val]) => (
                  <div key={label} style={styles.metaRow}>
                    <span style={styles.metaIcon}>{icon}</span>
                    <span style={styles.metaLabel}>{label}:</span>
                    <span style={styles.metaVal}>{val}</span>
                  </div>
                ))}
              </div>

              {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

              {owned ? (
                <Link to={`/course/${id}/learn`} className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                  Continue Learning →
                </Link>
              ) : inCart ? (
                <Link to="/cart" className="btn btn-secondary btn-lg" style={{ width: '100%' }}>
                  View Cart →
                </Link>
              ) : (
                <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handleAddToCart} disabled={adding}>
                  {adding ? 'Adding...' : 'Add to Cart — $' + parseFloat(course.price).toFixed(2)}
                </button>
              )}

              <div style={styles.guarantee}>
                🔒 Secure checkout • Instant access after purchase
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  breadcrumb: { marginBottom: 24 },
  breadLink: { fontSize: 14, color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 500 },
  layout: { display: 'grid', gridTemplateColumns: '1fr 360px', gap: 48, alignItems: 'start' },
  main: { display: 'flex', flexDirection: 'column', gap: 32 },
  categoryRow: { display: 'flex', gap: 8 },
  title: { fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--slate-900)', lineHeight: 1.25 },
  description: { fontSize: 16, color: 'var(--slate-600)', lineHeight: 1.75 },
  learnBox: { background: 'var(--teal-50)', border: '1px solid var(--teal-100)', borderRadius: 12, padding: '24px' },
  learnTitle: { fontSize: 17, fontWeight: 700, marginBottom: 16, color: 'var(--slate-900)' },
  learnGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  learnItem: { display: 'flex', gap: 10, fontSize: 14, color: 'var(--slate-700)' },
  checkMark: { color: 'var(--teal-600)', fontWeight: 700, flexShrink: 0 },
  sectionsBox: { border: '1px solid var(--color-border)', borderRadius: 12, padding: '24px' },
  sectionItem: { display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--color-border)', fontSize: 15, alignItems: 'center' },
  sectionNum: { width: 24, height: 24, borderRadius: '50%', background: 'var(--teal-100)', color: 'var(--teal-700)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sidebar: { position: 'sticky', top: 88 },
  sideCard: { border: '1px solid var(--color-border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-lg)' },
  sideHeader: { background: 'linear-gradient(135deg, #0f766e, #0d9488)', padding: '24px', textAlign: 'center' },
  price: { fontSize: 40, fontWeight: 800, color: 'white' },
  priceNote: { fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 4 },
  sideMeta: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid var(--color-border)' },
  metaRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 },
  metaIcon: { fontSize: 16, flexShrink: 0 },
  metaLabel: { color: 'var(--color-text-muted)', flexShrink: 0 },
  metaVal: { color: 'var(--slate-800)', fontWeight: 600, marginLeft: 'auto' },
  guarantee: { padding: '12px 24px', fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' },
};
